import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../hooks/useAuth";
import { useReferral } from "../contexts/ReferralContext";
import { Logo, Input, Button, WelcomeBanner } from "../components";
import SoulumoWord from "../components/SoulumoWord";
import { resolveBrand } from "../lib/brand";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";

// Forest hero image for auth pages
const AUTH_HERO = "https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&w=1280&q=80";

export const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { hasReferral } = useReferral();
  const navigate = useNavigate();

  // Oct 4 2026 · If a tester lands on /login on soulumo.app we were
  // showing the Reclaim-branded screen (RECLAIM wordmark, pink/cream
  // palette). Redirect to the Soulumo-themed sign-in instead so the
  // chrome always matches the host they arrived on. Preserves any
  // ?redirect= param so post-login routing still works.
  if (typeof window !== "undefined" && resolveBrand() === "soulumo") {
    const q = window.location.search || "";
    return <Navigate to={`/soulumo/join?mode=signin${q ? `&${q.slice(1)}` : ""}`} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      // Honour ?redirect= so users returning from gated pages (e.g.
      // /admin/excelior-training) land back where they were sent from.
      const params = new URLSearchParams(window.location.search);
      const redirect = params.get("redirect");
      const safeRedirect = redirect && redirect.startsWith("/") ? redirect : "/";
      navigate(safeRedirect);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7] flex flex-col pb-safe">
      {/* Hero Section */}
      <div 
        className="h-[25vh] min-h-[180px] relative bg-cover bg-center flex-shrink-0"
        style={{ backgroundImage: `url(${AUTH_HERO})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1D1A]/50 via-[#1A1D1A]/30 to-[#FAF9F7]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FAF9F7] via-transparent to-transparent" />
        
        {/* Logo centered on hero */}
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Logo size="lg" variant="light" />
            <p className="text-white/80 tracking-[0.2em] text-xs font-medium mt-3 uppercase">School of Consciousness</p>
          </motion.div>
        </div>
      </div>

      {/* Form Section - scrollable with keyboard padding */}
      <div className="flex-1 flex flex-col justify-start px-6 py-6 pb-[300px] overflow-y-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="max-w-sm mx-auto w-full space-y-6"
        >
          {/* Welcome Banner for referred users */}
          {hasReferral && (
            <WelcomeBanner className="animate-fade-in-up" />
          )}

          {/* Welcome text */}
          <div className="text-center space-y-2">
            <p
              style={{
                color: 'rgba(139,94,52,0.65)',
                fontSize: '10px',
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                fontWeight: 400,
              }}
            >
              Reclaim
            </p>
            <h1
              className="text-3xl text-[#1A1D1A]"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 400 }}
            >
              Welcome Back
            </h1>
            <div
              aria-hidden
              style={{
                height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(217,180,120,0.55), transparent)',
                margin: '12px auto 0',
                width: '64px',
              }}
            />
            <p className="text-[#7A7F7A] text-sm pt-2">Continue your journey of transformation</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="login-email"
              required
            />
            
            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                data-testid="login-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-[42px] text-[#9A9F9A] hover:text-[#4A4F4A] transition-colors"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-[#9B4343]/10 border border-[#9B4343]/20 rounded-xl"
              >
                <p className="text-[#9B4343] text-sm text-center">{error}</p>
              </motion.div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-2xl text-white font-medium transition-all"
              style={{
                background: loading
                  ? 'linear-gradient(180deg, rgba(45,74,42,0.55) 0%, rgba(28,46,26,0.55) 100%)'
                  : 'linear-gradient(180deg, #2D4A2A 0%, #1c2e1a 100%)',
                border: '1px solid rgba(241,210,156,0.32)',
                boxShadow: '0 0 32px rgba(241,210,156,0.10), inset 0 1px 0 rgba(241,210,156,0.18)',
              }}
              data-testid="login-submit"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          {/* Links */}
          <div className="space-y-4 text-center">
            <button
              onClick={() => navigate("/forgot-password")}
              className="text-sm text-[#2D4A2A] hover:underline underline-offset-4 font-semibold"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </button>
            <p className="text-[#7A7F7A]">
              Don't have an account?{" "}
              <button
                onClick={() => navigate("/register")}
                className="text-[#2D4A2A] font-semibold hover:underline underline-offset-4"
                data-testid="register-link"
              >
                Sign up
              </button>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { hasReferral, getReferralCode, clearReferral } = useReferral();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // Include referral code if present
      const referralCode = getReferralCode();
      await register(name, email, password, referralCode);
      
      // Clear referral after successful registration
      if (referralCode) {
        clearReferral();
      }
      const redirect = new URLSearchParams(window.location.search).get("redirect");
      navigate(redirect && redirect.startsWith("/") && !redirect.startsWith("//") ? redirect : "/");
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex flex-col justify-center px-6 py-12">
      <div className="max-w-sm mx-auto w-full space-y-10">
        {/* Welcome Banner for referred users */}
        {hasReferral && (
          <WelcomeBanner className="animate-fade-in-up" />
        )}

        {/* Logo & Header */}
        <div className="text-center space-y-4 animate-fade-in-up">
          <div className="flex justify-center mb-6">
            <Logo size="lg" variant="dark" />
          </div>
          <p className="text-[#6B7280] tracking-wide">Begin your journey</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5 animate-fade-in-up stagger-1">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            data-testid="register-name"
            required
          />
          
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            data-testid="register-email"
            required
          />
          
          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
              data-testid="register-password"
              required
              minLength={6}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-11 text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border border-red-100 rounded-xl animate-fade-in">
              <p className="text-red-600 text-sm text-center">{error}</p>
            </div>
          )}

          <Button 
            type="submit" 
            disabled={loading} 
            className="w-full" 
            data-testid="register-submit"
          >
            {loading ? "Creating account..." : "Create Account"}
          </Button>
        </form>

        {/* Link */}
        <p className="text-[#6B7280] text-center animate-fade-in-up stagger-2">
          Already have an account?{" "}
          <button
            onClick={() => navigate("/login")}
            className="text-[#4A6741] font-medium hover:underline"
            data-testid="login-link"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [emailActuallySent, setEmailActuallySent] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Oct 3 2026 · Michael — a tester on soulumo.app saw the RECLAIM
  // logo on this page and (rightly) felt it was a branding leak.
  // Detect the current brand and swap palette + logo + copy so the
  // forgot-password surface always matches the domain the user
  // arrived on.
  const brand = typeof window !== "undefined" ? resolveBrand() : "reclaim";
  const isSoulumo = brand === "soulumo";

  // Where "Back to login" should return the user. On soulumo.app
  // this is /soulumo/join (which also hosts sign-in); elsewhere the
  // generic /login still applies.
  const backTarget = isSoulumo ? "/soulumo/join?mode=signin" : "/login";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { authAPI } = await import("../services/api");
      const resp = await authAPI.forgotPassword(email);
      // Backend now returns { email_sent: bool, message: str } — trust
      // that boolean so we NEVER lie to the user about email delivery.
      const wasSent = resp?.data?.email_sent !== false;
      setEmailActuallySent(wasSent);
      setSent(true);
    } catch {
      // Network failure — treat as delivery failure, honest state.
      setEmailActuallySent(false);
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  // ── Soulumo-branded surface (navy + champagne) ───────────────────
  if (isSoulumo) {
    const TOK = {
      bg: "radial-gradient(ellipse at 30% 20%, #16304f 0%, #0d1f3a 42%, #080f1f 100%)",
      ink: "#0d1f3a",
      cream: "#f5ecd9",
      creamDim: "rgba(245,236,217,0.78)",
      creamMist: "rgba(245,236,217,0.5)",
      gold: "#e0a97a",
      goldSoft: "rgba(224,169,122,0.86)",
      goldMist: "rgba(224,169,122,0.28)",
      cardBg: "rgba(245,236,217,0.04)",
      cardBorder: "rgba(224,169,122,0.22)",
      danger: "#e78b7a",
    };
    const SERIF = '"Cormorant Garamond", Georgia, serif';
    const SANS = '"Manrope", "Helvetica Neue", Arial, sans-serif';
    const fieldStyle = {
      width: "100%",
      background: "rgba(0,0,0,0.3)",
      border: `1px solid ${TOK.goldMist}`,
      color: TOK.cream,
      borderRadius: 999,
      padding: "12px 20px",
      fontFamily: SANS,
      fontSize: "0.92rem",
      minHeight: 44,
      outline: "none",
    };

    return (
      <div
        className="min-h-screen flex flex-col justify-center px-6 py-12 relative overflow-hidden"
        style={{ background: TOK.bg, color: TOK.cream }}
        data-testid="forgot-password-soulumo"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute"
          style={{
            top: "-18%", left: "50%", transform: "translateX(-50%)",
            width: 620, height: 620,
            background:
              "radial-gradient(circle, rgba(224,169,122,0.22) 0%, rgba(224,169,122,0.05) 32%, transparent 68%)",
            filter: "blur(32px)",
          }}
        />
        <div className="max-w-sm mx-auto w-full space-y-8 relative z-10">
          <button
            onClick={() => navigate(backTarget)}
            className="flex items-center gap-2 transition"
            style={{
              color: TOK.creamMist,
              fontFamily: SANS,
              fontSize: "0.72rem",
              letterSpacing: "0.14em",
              minHeight: 44,
            }}
            data-testid="forgot-back"
          >
            <ArrowLeft size={16} /> Back to sign in
          </button>

          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <SoulumoWord size="lg" />
            </div>
            <p
              style={{
                color: TOK.goldSoft,
                fontFamily: SANS,
                fontSize: "0.62rem",
                letterSpacing: "0.4em",
                textTransform: "uppercase",
              }}
            >
              Reset your password
            </p>
          </div>

          {sent ? (
            emailActuallySent ? (
              <div
                className="rounded-2xl p-7 text-center space-y-4"
                style={{ background: TOK.cardBg, border: `1px solid ${TOK.cardBorder}` }}
                data-testid="forgot-sent-ok"
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mx-auto"
                  style={{ background: "rgba(224,169,122,0.14)", border: `1px solid ${TOK.goldMist}` }}
                >
                  <span style={{ color: TOK.gold, fontSize: "1.4rem" }}>✓</span>
                </div>
                <h3
                  className="italic font-light"
                  style={{ fontFamily: SERIF, fontSize: "1.5rem", color: TOK.cream }}
                >
                  Check your email
                </h3>
                <p
                  className="italic"
                  style={{ fontFamily: SERIF, fontSize: "1rem", color: TOK.creamDim, lineHeight: 1.55 }}
                >
                  If an account exists with that email, reset instructions are on the way.
                </p>
              </div>
            ) : (
              <div
                className="rounded-2xl p-7 text-center space-y-4"
                style={{ background: "rgba(231,139,122,0.06)", border: `1px solid rgba(231,139,122,0.3)` }}
                data-testid="forgot-sent-failed"
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mx-auto"
                  style={{ background: "rgba(231,139,122,0.14)", border: `1px solid rgba(231,139,122,0.4)` }}
                >
                  <span style={{ color: TOK.danger, fontSize: "1.4rem" }}>!</span>
                </div>
                <h3
                  className="italic font-light"
                  style={{ fontFamily: SERIF, fontSize: "1.5rem", color: TOK.cream }}
                >
                  We couldn&apos;t send that email
                </h3>
                <p
                  className="italic"
                  style={{ fontFamily: SERIF, fontSize: "1rem", color: TOK.creamDim, lineHeight: 1.55 }}
                >
                  Something stopped the reset email reaching you. Message
                  Michael on WhatsApp — he can send you a one-tap sign-in
                  link directly.
                </p>
                <a
                  href="https://wa.me/447000000000?text=Hi%20Michael%2C%20I%27m%20stuck%20resetting%20my%20Soulumo%20password.%20Can%20you%20send%20me%20a%20one-tap%20sign-in%20link%3F"
                  className="inline-flex items-center justify-center rounded-full"
                  style={{
                    background: TOK.gold,
                    color: TOK.ink,
                    fontFamily: SANS,
                    fontSize: "0.76rem",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    padding: "10px 22px",
                    minHeight: 42,
                  }}
                  data-testid="forgot-whatsapp"
                  target="_blank"
                  rel="noreferrer"
                >
                  Message Michael on WhatsApp
                </a>
                <div>
                  <button
                    onClick={() => { setSent(false); setEmailActuallySent(true); }}
                    style={{ color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.78rem" }}
                    data-testid="forgot-try-again"
                    className="underline-offset-2 hover:underline"
                  >
                    Try again
                  </button>
                </div>
              </div>
            )
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                style={fieldStyle}
                data-testid="forgot-email"
                autoComplete="email"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-full disabled:opacity-60"
                style={{
                  background: TOK.gold,
                  color: TOK.ink,
                  fontFamily: SANS,
                  fontSize: "0.78rem",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  padding: "14px 22px",
                  minHeight: 48,
                }}
                data-testid="forgot-submit"
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>
            </form>
          )}

          <p
            className="text-center italic"
            style={{ color: TOK.creamMist, fontFamily: SERIF, fontSize: "0.82rem" }}
          >
            Soulumo · soulumo.app
          </p>
        </div>
      </div>
    );
  }

  // ── Default Reclaim-branded surface (unchanged) ───────────────────
  return (
    <div className="min-h-screen bg-[#FAFAF7] flex flex-col justify-center px-6 py-12">
      <div className="max-w-sm mx-auto w-full space-y-10">
        <button
          onClick={() => navigate(backTarget)}
          className="flex items-center gap-2 text-[#6B7280] hover:text-[#4A6741] transition-colors animate-fade-in"
          data-testid="forgot-back"
        >
          <ArrowLeft size={18} /> Back to login
        </button>

        <div className="text-center space-y-4 animate-fade-in-up">
          <div className="flex justify-center mb-6">
            <Logo size="lg" variant="dark" />
          </div>
          <p className="text-[#6B7280] tracking-wide">Reset your password</p>
        </div>

        {sent ? (
          emailActuallySent ? (
            <div className="bg-white rounded-2xl shadow-lg border border-[#E5E7EB]/60 p-8 text-center space-y-5 animate-fade-in-up" data-testid="forgot-sent-ok">
              <div className="w-16 h-16 bg-gradient-to-br from-[#4A6741]/10 to-[#4A6741]/5 rounded-full flex items-center justify-center mx-auto">
                <span className="text-[#4A6741] text-2xl">✓</span>
              </div>
              <h3 className="font-heading text-xl text-[#1F2937]">Check your email</h3>
              <p className="text-[#6B7280]">
                If an account exists with that email, we&apos;ve sent password reset instructions.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg border border-red-200/70 p-8 text-center space-y-5 animate-fade-in-up" data-testid="forgot-sent-failed">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto">
                <span className="text-red-600 text-2xl">!</span>
              </div>
              <h3 className="font-heading text-xl text-[#1F2937]">We couldn&apos;t send that email</h3>
              <p className="text-[#6B7280]">
                Our email service is unavailable right now. Please contact Michael on WhatsApp — he can share a one-tap sign-in link with you directly.
              </p>
              <button
                onClick={() => { setSent(false); setEmailActuallySent(true); }}
                className="text-sm text-[#4A6741] hover:underline"
                data-testid="forgot-try-again"
              >
                Try again
              </button>
            </div>
          )
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5 animate-fade-in-up stagger-1">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="forgot-email"
              required
            />
            <Button
              type="submit"
              disabled={loading}
              className="w-full"
              data-testid="forgot-submit"
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};
